@if (loading) {
  <div class="flex items-center justify-center h-full w-full gap-2">
    <fa-icon [icon]="faSpinnerThird" class="text-gray-500 animate-spin text-svg-16"></fa-icon>
    <span class="text-gray-400 text-sm">Workflow wird geladen…</span>
  </div>
} @else {
  <div class="flex flex-col h-full w-full bg-white">
    <app-page-header class="relative z-20">
      <div class="flex flex-row items-center gap-4 w-full">
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <span class="text-xl font-medium text-gray-900 truncate">{{ workflowName }}</span>
        </div>

        <div class="flex items-center gap-4">
          @if (!readOnly) {
            @if (canRuleTest) {
              <!-- Testlauf (Figma „Testmodus" 01/06): testet den ungespeicherten Workflow dieser Regel. -->
              <span class="relative inline-flex" [tooltip]="ruleTestTooltip" [tooltipSubtext]="ruleTestTooltipSubtext"
                    tooltipDirection="bottom">
                <bic-button (onClick)="openRuleTest()" [disabled]="ruleTestLimitReached" buttonType="white">
                  <fa-icon iconLeft [icon]="faPlay"></fa-icon>
                  Testlauf
                </bic-button>
                @if (ruleTestUnseen) {
                  <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green" aria-label="Neues Testergebnis"></span>
                }
              </span>
            }
            <bic-button (onClick)="goBack()" buttonType="white">
              Abbrechen
            </bic-button>

            <bic-button (onClick)="saveWorkflow()"
                        [disabled]="!definitionId || definitionId === 'new'"
                        [loading]="saving"
                        buttonType="primary">
              Übernehmen
            </bic-button>
          }
        </div>
      </div>
    </app-page-header>

    <!-- Vorschlag von chatBIC für diese Regel: der Ablauf liegt schon auf der Leinwand, neue und
         geänderte Bausteine sind markiert. Gespeichert wird erst mit „Änderungen übernehmen". -->
    @if (pendingProposal) {
      <div class="mx-4 mt-3 relative z-20 flex flex-col gap-2">
        <app-proposal-banner
          (applyClicked)="onRuleProposalApplyClicked()"
          (discardClicked)="onRuleProposalDiscardClicked()"
          [proposal]="pendingProposal">
        </app-proposal-banner>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 px-1">
          <span class="inline-flex items-center gap-1.5">
            <span class="inline-block w-3 h-3 rounded border-2 border-dashed border-[#2563EB]"></span>Neu
          </span>
          <span class="inline-flex items-center gap-1.5">
            <span class="inline-block w-3 h-3 rounded border-2 border-dashed border-[#F59E0B]"></span>Geändert
          </span>
          @if (proposalRemovedNames.length) {
            <span>Entfernt: {{ proposalRemovedNames.join(', ') }}</span>
          }
        </div>
      </div>
    }

    @if (validationWarnings.length > 0) {
      <div class="mx-4 mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg relative z-20">
        <p class="text-sm font-medium text-amber-800 mb-1">
          Hinweis — diese Zweige liefern kein Ergebnis:
        </p>
        <ul class="list-disc list-inside space-y-1">
          @for (warning of validationWarnings; track warning) {
            <li class="text-sm text-amber-800">{{ warning }}</li>
          }
        </ul>
      </div>
    }

    @if (validationErrors.length > 0) {
      <div class="mx-4 mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg relative z-20">
        <p class="text-sm font-medium text-yellow-800 mb-1">
          Die Prüfregel ist noch unvollständig. Bitte ergänze:
        </p>
        <ul class="list-disc list-inside space-y-1">
          @for (error of validationErrors; track error) {
            <li class="text-sm text-yellow-800">{{ error }}</li>
          }
        </ul>
      </div>
    }

    <div class="flex flex-1 min-h-0">
      <!-- Canvas + Log Panel -->
      <div class="flex-1 min-w-0 relative flex flex-col">
        <div class="flex-1 min-h-0 flow-canvas-bg relative overflow-hidden">
          <app-workflow-canvas (nodeSelected)="onNodeSelected($event)"
                               (connectionCreated)="onConnectionCreated($event)"
                               (nodeDoubleClicked)="onNodeDoubleClicked($event)"
                               (eingangTriggerDropped)="onEingangTriggerDropped()"
                               [edges]="edges"
                               [manifest]="manifest"
                               [modules]="allModules"
                               [readOnly]="readOnly"
                               [rightOverlayWidth]="sidebarOverlayWidth"
                               [stepStatuses]="stepStatuses"
                               [proposalMarks]="proposalMarks"
                               class="block w-full h-full"/>

          <!-- Breadcrumb for drill-down navigation -->
          @if (drillDownStack.length > 0) {
            <div
              class="absolute top-3 left-3 z-30 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full shadow-sm">
              <button (click)="drillBack()"
                      class="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium">
                <fa-icon [icon]="faChevronLeft" class="text-[10px]"></fa-icon>
                {{ drillDownStack[drillDownStack.length - 1].workflowName || 'Gesamtworkflow' }}
              </button>
              <span class="text-gray-400 text-sm">/</span>
              <span class="text-sm font-medium text-gray-900 truncate max-w-[200px]">{{ workflowName }}</span>
            </div>
          }

          <!-- ─── Central Toolbar ─── -->
          <div class="absolute top-3 left-0 right-0 z-10 flex justify-center pointer-events-none">
            <div class="flex items-center gap-0.5 h-10
                    bg-white border border-gray-200 rounded-full shadow-sm
                    px-1.5 pointer-events-auto">

              <!-- Group 1: Undo / Redo -->
              <button (click)="canvas?.undo()" [class.cursor-not-allowed]="!canvas?.canUndo"
                      [class.hover:bg-gray-100]="canvas?.canUndo"
                      [class.opacity-40]="!canvas?.canUndo"
                      [disabled]="!canvas?.canUndo"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Rückgängig (Strg+Z)'" class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [icon]="faRotateLeft" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.redo()" [class.cursor-not-allowed]="!canvas?.canRedo"
                      [class.hover:bg-gray-100]="canvas?.canRedo"
                      [class.opacity-40]="!canvas?.canRedo"
                      [disabled]="!canvas?.canRedo"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Wiederholen (Strg+Y)'" class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [icon]="faRotateRight" class="text-[13px] text-gray-600"></fa-icon>
              </button>

              <div class="w-px h-5 bg-gray-200 mx-1"></div>

              <!-- Group 2: Clipboard -->
              <button (click)="canvas?.copySelection()" [class.cursor-not-allowed]="!hasSelection"
                      [class.hover:bg-gray-100]="hasSelection"
                      [class.opacity-40]="!hasSelection"
                      [disabled]="!hasSelection"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Kopieren (Strg+C)'" class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [icon]="faCopy" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.pasteClipboard()" [class.cursor-not-allowed]="!canvas?.hasClipboard"
                      [class.hover:bg-gray-100]="canvas?.hasClipboard"
                      [class.opacity-40]="!canvas?.hasClipboard"
                      [disabled]="!canvas?.hasClipboard"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Einfügen (Strg+V)'" class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [icon]="faPaste" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.duplicateSelection()" [class.cursor-not-allowed]="!hasSelection"
                      [class.hover:bg-gray-100]="hasSelection"
                      [class.opacity-40]="!hasSelection"
                      [disabled]="!hasSelection"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Duplizieren (Strg+D)'" class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [icon]="faClone" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.deleteSelectedNodes()" [class.cursor-not-allowed]="!hasSelection"
                      [class.hover:bg-gray-100]="hasSelection"
                      [class.opacity-40]="!hasSelection"
                      [disabled]="!hasSelection"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Löschen (Entf)'" class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [icon]="faTrashCan" class="text-[13px] text-gray-600"></fa-icon>
              </button>

              <div class="w-px h-5 bg-gray-200 mx-1"></div>

              <!-- Group 3: Layout -->
              <button (click)="canvas?.autoLayout()"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Auto-Layout'" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors">
                <fa-icon [icon]="faDiagramProject" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.fitToView()"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Ansicht einpassen'" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors">
                <fa-icon [icon]="faExpand" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.toggleSnap()"
                      [class.bg-gray-100]="canvas?.snapEnabled"
                      [class.hover:bg-gray-100]="!canvas?.snapEnabled"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="canvas?.snapEnabled ? 'Snap-to-Grid deaktivieren' : 'Snap-to-Grid aktivieren'"
                      class="flex items-center justify-center w-8 h-8 rounded-full transition-colors">
                <fa-icon [class.text-gray-600]="!canvas?.snapEnabled" [class.text-green-600]="canvas?.snapEnabled" [icon]="faMagnet"
                         class="text-[13px]"></fa-icon>
              </button>

              <div class="w-px h-5 bg-gray-200 mx-1"></div>

              <!-- Group 4: Zoom -->
              <button (click)="canvas?.zoomOut()"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Herauszoomen'" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors">
                <fa-icon [icon]="faMagnifyingGlassMinus" class="text-[13px] text-gray-600"></fa-icon>
              </button>
              <button (click)="canvas?.zoomReset()"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Zoom zurücksetzen'" class="flex items-center justify-center px-2 h-8 rounded-full hover:bg-gray-100 transition-colors min-w-[42px]">
                <span class="text-xs font-medium text-gray-600 tabular-nums">{{ canvas?.zoomPercent ?? 100 }}%</span>
              </button>
              <button (click)="canvas?.zoomIn()"
                      [tooltipDirection]="'bottom'"
                      [tooltip]="'Heranzoomen'" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors">
                <fa-icon [icon]="faMagnifyingGlassPlus" class="text-[13px] text-gray-600"></fa-icon>
              </button>
            </div>
          </div>

        </div>

        <!-- Log Panel (floating) -->
        @if (showLogPanel) {
          <div [style.right]="showSidebar ? '424px' : '12px'"
               class="absolute bottom-3 left-3 z-20">
            <app-workflow-log-panel [logs]="instanceLogs"/>
          </div>
        }

        <!-- Sidebar (floating) -->
        <div
          class="absolute top-3 right-3 bottom-3 z-20 w-[400px] bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col overflow-hidden">
          @if (sidebarView === 'test-panel' && currentInstance) {
            <app-workflow-test-panel
              (childDrillDown)="drillIntoChild($event)"
              (closed)="onTestPanelClosed()"
              [childInstances]="hybridChildInstances"
              [instance]="currentInstance"
              [modules]="allModules"/>
          } @else if (sidebarView === 'step-detail' && selectedStepLog) {
              <!-- Step Detail View (Log mode: shows details for clicked node) -->
              <div class="flex flex-col h-full overflow-hidden">
                <!-- Header with back button -->
                <div class="flex items-center gap-2 px-4 py-2 border-b border-gray-200 shrink-0">
                  <button (click)="onStepDetailBack()"
                          class="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100">
                    <fa-icon [icon]="faChevronLeft" class="text-xs text-gray-500"></fa-icon>
                  </button>
                  <span
                    class="flex-1 text-base font-medium text-gray-900 truncate">{{ getStepModuleName(selectedStepLog) }}</span>
                  <button (click)="onTestPanelClosed()"
                          class="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100">
                    <fa-icon [icon]="faXmark" class="text-sm text-gray-500"></fa-icon>
                  </button>
                </div>

                <!-- Status -->
                <div [class.bg-gray-100]="selectedStepLog.status !== 'COMPLETED' && selectedStepLog.status !== 'FAILED'"
                     [class.bg-green-50]="selectedStepLog.status === 'COMPLETED'"
                     [class.bg-red-50]="selectedStepLog.status === 'FAILED'"
                     class="flex items-center gap-3 px-4 py-2 border-b border-gray-200">
                  <span [class.text-gray-700]="selectedStepLog.status !== 'COMPLETED' && selectedStepLog.status !== 'FAILED'"
                        [class.text-green-700]="selectedStepLog.status === 'COMPLETED'"
                        [class.text-red-700]="selectedStepLog.status === 'FAILED'"
                        class="text-sm font-medium">
                    {{ selectedStepLog.status === 'COMPLETED' ? 'Erfolgreich' : selectedStepLog.status === 'FAILED' ? 'Fehlgeschlagen' : 'Läuft' }}
                  </span>
                  @if (selectedStepLog.startedAt && selectedStepLog.completedAt) {
                    <span class="text-xs text-gray-500">{{ getStepDurationMs(selectedStepLog) }}</span>
                  }
                </div>

                <!-- Scrollable content -->
                <div class="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4">
                  <!-- Input Data -->
                  @if (selectedStepLog.inputData && hasStepEntries(selectedStepLog.inputData)) {
                    <div>
                      <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Eingaben</span>
                      <div class="mt-1.5 flex flex-col gap-1.5">
                        @for (entry of getStepDisplayEntries(selectedStepLog.inputData); track entry.key) {
                          <div class="flex flex-col gap-0.5">
                            <span class="text-xs font-medium text-gray-700">{{ entry.key }}</span>
                            <span
                              class="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200 break-all whitespace-pre-wrap">{{ entry.value }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }

                  <!-- Output Data -->
                  @if (selectedStepLog.outputData && hasStepEntries(selectedStepLog.outputData)) {
                    <div>
                      <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Ausgaben</span>
                      <div class="mt-1.5 flex flex-col gap-1.5">
                        @for (entry of getStepDisplayEntries(selectedStepLog.outputData); track entry.key) {
                          <div class="flex flex-col gap-0.5">
                            <span class="text-xs font-medium text-gray-700">{{ entry.key }}</span>
                            <span
                              class="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200 break-all whitespace-pre-wrap">{{ entry.value }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }

                  <!-- Error -->
                  @if (selectedStepLog.status === 'FAILED' && selectedStepLog.errorMessage) {
                    <div class="px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                      <span class="text-[11px] font-semibold text-red-600 uppercase tracking-wide">Fehler</span>
                      <p class="mt-1 text-xs text-red-700 whitespace-pre-wrap">{{ selectedStepLog.errorMessage }}</p>
                    </div>
                  }

                  <!-- Hybrid child instances for HYBRID_RULESET_EXECUTOR -->
                  @if (selectedStepLog.moduleId === 'HYBRID_RULESET_EXECUTOR' && hybridChildInstances.length > 0) {
                    <div>
                      <span
                        class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Prüfregel-Ergebnisse</span>
                      <div class="mt-1.5 flex flex-col gap-2">
                        @for (child of hybridChildInstances; track child.id) {
                          <div class="rounded-lg border border-gray-200 bg-white overflow-hidden">
                            <div class="flex items-center gap-2 px-3 py-2">
                              @if (child.status === 'COMPLETED') {
                                <fa-icon [icon]="faCircleCheck" class="text-sm text-green-500"></fa-icon>
                              } @else if (child.status === 'FAILED' || child.status === 'CANCELLED') {
                                <fa-icon [icon]="faCircleXmark" class="text-sm text-red-500"></fa-icon>
                              } @else {
                                <fa-icon [icon]="faSpinner" class="text-sm text-gray-400 animate-spin"></fa-icon>
                              }
                              <span
                                class="text-xs font-medium text-gray-900 flex-1 truncate">{{ child.workflowName || 'Prüfregel' }}</span>
                              @if (child.outputData?.['score'] != null) {
                                <span [class.bg-green-100]="child.outputData?.['score'] >= 0.7"
                                      [class.bg-red-100]="child.outputData?.['score'] < 0.7"
                                      [class.text-green-700]="child.outputData?.['score'] >= 0.7"
                                      [class.text-red-700]="child.outputData?.['score'] < 0.7"
                                      class="text-xs font-medium px-1.5 py-0.5 rounded">
                                  {{ (child.outputData?.['score'] * 100).toFixed(0) }}%
                                </span>
                              }
                              <button (click)="drillIntoChild(child)"
                                      class="flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 shrink-0"
                                      title="Workflow-Details anzeigen">
                                <fa-icon [icon]="faChevronLeft" class="text-[10px] text-gray-500 rotate-180"></fa-icon>
                              </button>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }

                  <!-- Empty state -->
                  @if (!hasStepEntries(selectedStepLog.inputData) && !hasStepEntries(selectedStepLog.outputData) && !selectedStepLog.errorMessage) {
                    <span class="text-sm text-gray-400 text-center py-4">Keine Daten für diesen Schritt verfügbar</span>
                  }
                </div>
              </div>
          } @else if (sidebarView === 'node-config' && (selectedStep || selectedTrigger)) {
            <app-workflow-node-config
              (closed)="onNodeConfigClosed()"
              (inputChanged)="onNodeConfigChanged($event)"
              (nameChanged)="onNodeNameChanged($event)"
              (rulesetSelected)="onRulesetAddedToWorkflow($event)"
              [module]="selectedModule"
              [projectId]="stateConfigService.selectedProject?.id ?? null"
              [step]="selectedStep"
              [allModules]="allModules"
              [manifest]="manifest"
              [readOnly]="readOnly"
              [nodeId]="selectedStep?.nodeId ?? selectedTrigger?.nodeId ?? null"
              [trigger]="selectedTrigger"/>
          } @else {
            <app-workflow-sidebar
              (searchQueryChange)="moduleSearchQuery = $event"
              [categories]="moduleCategories"
              [moduleTypeFilter]="isHybridMode ? 'PRUEFREGEL' : 'WORKFLOW'"
              [modules]="paletteModules"
              [searchQuery]="moduleSearchQuery"
              [sectionTitle]="sidebarTitle"
              [sharedCategories]="sharedCategories"/>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Settings Popup -->
  @if (showSettingsPopup) {
    <bic-popup (close)="onSettingsClose()" [title]="'Workflow anpassen'" customClass="w-[1000px]">
      <div bicPopupContent class="flex flex-col gap-4">
        <!-- Workflow Name -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-900">Workflow Name</label>
          <input [(ngModel)]="settingsName"
                 class="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-green-500 focus:ring-green-500 outline-none"
                 placeholder="Workflow Name"
                 type="text">
        </div>

        <!-- Workflow Beschreibung -->
        <div class="flex flex-col gap-1">
          <bic-textarea [(ngModel)]="settingsDescription"
                        [label]="'Workflow Beschreibung'"
                        placeholder="Beschreibung des Workflows"
                        [rows]="3"></bic-textarea>
        </div>

      </div>

      <div bicPopupButtonGroup class="flex flex-row justify-end gap-4">
        <bic-button (onClick)="onSettingsClose()" buttonType="white">Abbrechen</bic-button>
        <bic-button (onClick)="onSettingsSave()" buttonType="primary">Übernehmen</bic-button>
      </div>
    </bic-popup>
  }

  <!-- Verzweigung geändert: Rückfrage, weil Zweig-Verbindungen verloren gehen -->
  @if (branchLossMessage) {
    <bic-popup (close)="onBranchLossCancel()" [title]="'Verzweigung ändern'"
               customClass="w-[520px]">
      <div bicPopupContent class="flex flex-col gap-2">
        <p class="text-sm text-gray-900">{{ branchLossMessage }}</p>
        <p class="text-sm text-gray-600">
          Die Ausgänge des Knotens ergeben sich aus seiner Konfiguration. Verbindungen von Zweigen,
          die es weiterhin gibt, bleiben erhalten.
        </p>
      </div>

      <div bicPopupButtonGroup class="flex flex-row justify-end gap-4">
        <bic-button (onClick)="onBranchLossCancel()" buttonType="white">Abbrechen</bic-button>
        <bic-button (onClick)="onBranchLossConfirm()" buttonType="primary">
          Verbindungen entfernen
        </bic-button>
      </div>
    </bic-popup>
  }
}

@if (ruleTestContext) {
  <app-rule-test-popup [context]="ruleTestContext" (closed)="closeRuleTest()"></app-rule-test-popup>
}
